package ru.chm.gradereport.ui.screens.reviewers

import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import ru.chm.gradereport.data.GradeApi
import ru.chm.gradereport.model.CourseDto
import ru.chm.gradereport.model.ReviewerDto
import ru.chm.gradereport.ui.components.ConfirmDialog
import ru.chm.gradereport.ui.components.ErrorBanner
import ru.chm.gradereport.ui.components.LoadingBox
import ru.chm.gradereport.ui.components.ScreenHeader
import ru.chm.gradereport.ui.components.ScreenScaffold
@Composable
fun ReviewersScreen(
    api: GradeApi,
) {
    var reviewers by remember { mutableStateOf<List<ReviewerDto>>(emptyList()) }
    var courses by remember { mutableStateOf<List<CourseDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var login by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf("") }
    var assigningId by remember { mutableStateOf<String?>(null) }
    var selectedCourses by remember { mutableStateOf<Set<String>>(emptySet()) }
    var resettingPasswordId by remember { mutableStateOf<String?>(null) }
    var newPassword by remember { mutableStateOf("") }
    var pendingDeleteReviewer by remember { mutableStateOf<ReviewerDto?>(null) }
    val scope = rememberCoroutineScope()

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                reviewers = api.listReviewers()
                courses = api.listCourses()
            } catch (e: Exception) {
                error = e.message
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    ScreenScaffold {
        ScreenHeader(
            title = "Проверяющие",
            subtitle = "Управление доступом к курсам",
        )
        ErrorBanner(error) { error = null }

        CreateReviewerCard(
            displayName = displayName,
            onDisplayNameChange = { displayName = it },
            login = login,
            onLoginChange = { login = it },
            password = password,
            onPasswordChange = { password = it },
            onCreate = {
                scope.launch {
                    try {
                        api.createReviewer(
                            login.trim(),
                            password,
                            displayName.trim().ifBlank { login.trim() },
                        )
                        login = ""
                        password = ""
                        displayName = ""
                        reload()
                    } catch (e: Exception) {
                        error = e.message
                    }
                }
            },
        )

        if (loading) {
            LoadingBox()
        } else {
            reviewers.forEach { reviewer ->
                ReviewerCard(
                    reviewer = reviewer,
                    courses = courses,
                    isAssigning = assigningId == reviewer.id,
                    selectedCourses = selectedCourses,
                    onSelectedCoursesChange = { selectedCourses = it },
                    onStartAssign = {
                        assigningId = reviewer.id
                        selectedCourses = reviewer.courseIds.toSet()
                        resettingPasswordId = null
                        newPassword = ""
                    },
                    onCancelAssign = { assigningId = null },
                    onSaveAssign = {
                        scope.launch {
                            try {
                                api.assignCourses(reviewer.id, selectedCourses.toList())
                                assigningId = null
                                reload()
                            } catch (e: Exception) {
                                error = e.message
                            }
                        }
                    },
                    isResettingPassword = resettingPasswordId == reviewer.id,
                    newPassword = newPassword,
                    onNewPasswordChange = { newPassword = it },
                    onStartResetPassword = {
                        resettingPasswordId = reviewer.id
                        newPassword = ""
                        assigningId = null
                    },
                    onCancelResetPassword = {
                        resettingPasswordId = null
                        newPassword = ""
                    },
                    onSaveResetPassword = {
                        scope.launch {
                            try {
                                api.resetReviewerPassword(reviewer.id, newPassword)
                                resettingPasswordId = null
                                newPassword = ""
                            } catch (e: Exception) {
                                error = e.message
                            }
                        }
                    },
                    onDelete = { pendingDeleteReviewer = reviewer },
                )
            }
        }
    }

    pendingDeleteReviewer?.let { reviewer ->
        ConfirmDialog(
            title = "Удалить проверяющего?",
            message = "Проверяющий «${reviewer.displayName}» (${reviewer.login}) будет удалён. Назначения на курсы тоже снимутся.",
            onConfirm = {
                scope.launch {
                    try {
                        api.deleteReviewer(reviewer.id)
                        pendingDeleteReviewer = null
                        reload()
                    } catch (e: Exception) {
                        pendingDeleteReviewer = null
                        error = e.message
                    }
                }
            },
            onDismiss = { pendingDeleteReviewer = null },
        )
    }
}
